import { useEffect, useState, useCallback } from 'react';

/**
 * View mode — "designer" or "developer".
 *
 * Persistence model:
 *   1. URL query param `?view=dev|design`  (shareable deep links win
 *      if explicitly present at page load)
 *   2. localStorage `viewMode`              (in-session memory only —
 *      cleared at the top of index.js on every page load so refreshes
 *      always show the splash chooser again)
 *
 * The clear-on-load behavior is what gives the visitor a fresh
 * splash on every refresh while still preserving the choice during
 * in-app navigation (e.g. clicking About and returning home).
 *
 * setViewMode writes to localStorage but does NOT touch the URL —
 * otherwise the `?view=` would survive a refresh and skip the splash.
 */

const STORAGE_KEY = 'viewMode';
const EVENT_NAME = 'viewmode:change';

function normalizeMode(raw) {
  if (raw === 'dev' || raw === 'developer') return 'developer';
  return 'designer';
}

function readInitial(search) {
  try {
    const fromURL = new URLSearchParams(search).get('view');
    if (fromURL) return normalizeMode(fromURL);
    const fromStorage = localStorage.getItem(STORAGE_KEY);
    if (fromStorage) return normalizeMode(fromStorage);
  } catch (e) {}
  return 'designer';
}

/**
 * Has the visitor explicitly chosen a view yet? Used by the splash
 * chooser on the home page — if true, skip the splash and render
 * the underlying view directly. Also used by AgentWidget to hide
 * the chat FAB during the 50/50 splash screen.
 */
export function hasExplicitViewMode() {
  try {
    if (typeof window === 'undefined') return false;
    if (new URLSearchParams(window.location.search).get('view')) return true;
    if (localStorage.getItem(STORAGE_KEY)) return true;
  } catch (e) {}
  return false;
}

/**
 * Reactive version of hasExplicitViewMode — re-evaluates whenever
 * viewmode:change fires so consumers (AgentWidget, etc.) can flip
 * UI based on whether the splash has been committed.
 */
export function useHasChosenView() {
  const [chosen, setChosen] = useState(() => hasExplicitViewMode());

  useEffect(() => {
    const handler = () => setChosen(hasExplicitViewMode());
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  return chosen;
}

export function useViewMode() {
  const [mode, setMode] = useState(() => {
    if (typeof window === 'undefined') return 'designer';
    return readInitial(window.location.search);
  });

  // Listen for cross-component view changes via a custom event so
  // multiple consumers of useViewMode stay in sync without a context
  // provider.
  useEffect(() => {
    const handler = () => setMode(readInitial(window.location.search));
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  const setViewMode = useCallback((next) => {
    const normalized = normalizeMode(next);
    try {
      localStorage.setItem(STORAGE_KEY, normalized);
    } catch (e) {}
    // Notify other components. The URL is intentionally NOT updated
    // here — keeping `?view=` out of the URL ensures a refresh always
    // re-shows the splash.
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  }, []);

  return [mode, setViewMode];
}
